import React, { useEffect, useState, useRef, useCallback } from "react";
import { AmountDivideByHundred } from "../../../helper";
import { eventsPageSplitUpApi, expireHoldToken } from "../../../api";
import ChooseSeats from "./chooseSeats";
import * as moment from "moment-timezone";



const AddProducts = (props) => {
    const {
        eventDetails,
        eventPageProduct,
        orderItemIndex,
        lineItems,
        setOrderItems,
        getOperatingTime,
        notify,
        setShowButton,
        setFullScreen,
        mobileView,
        handleNext,
        selectedDate,
        setSelectedDate,
    } = props;

    const scrollRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const checkScroll = useCallback(() => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
            setCanScrollLeft(scrollLeft > 5);
            setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 5);
        }
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (el) {
            checkScroll();
            el.addEventListener("scroll", checkScroll);
            window.addEventListener("resize", checkScroll);
            
            const timer = setTimeout(checkScroll, 500);
            
            return () => {
                el.removeEventListener("scroll", checkScroll);
                window.removeEventListener("resize", checkScroll);
                clearTimeout(timer);
            };
        }
    }, [eventDetails?.eventPageDateTimes, checkScroll]);

    const scrollLeftFn = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
        }
    };

    const scrollRightFn = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
        }
    };

    const [openModifierGid, setOpenModifierGid] = useState(null);
    const [openItemsGid, setOpenItemsGid] = useState(null);
    const [validationTriggered, setValidationTriggered] = useState(false);
    const [updates, setUpdates] = useState(() => {
        return (lineItems || []).map(li => ({
            name: li.name,
            price: li.price,
            customItemAmount: li.customItemAmount != null ? li.customItemAmount : null,
            eventPageTicketTypeGid: li.eventPageTicketTypeGid || null,
            quantity: li.quantity,
            seatLabel: li.seatLabel,
            seats: li.seats || [],
            totalPrice: li.totalPrice || (li.price * li.quantity + (li.lineItemModifiers?.reduce((s, m) => s + (m.price * m.quantity), 0) || 0) + (li.lineItemItems?.reduce((sm, n) => sm + (n.price * n.quantity), 0) || 0)),
            lineItemModifiers: li.lineItemModifiers || [],
            lineItemItems: li.lineItemItems || [],
            lineItemDiscounts: li.lineItemDiscounts || [],
            productGid: li.productGid || eventPageProduct?.gid,
            customItemGid: li.customItemGid,
            itemGid: li.itemGid,
            listedItem: li.listedItem,
            duplicateIndex: li.duplicateIndex,
            lineItemKey: li.lineItemKey || li.seatLabel || `ticket-${Date.now()}-${Math.random()}`,
        }));
    });



    // API Calculation States
    const [subtotal, setSubtotal] = useState(0.0);
    const [total, setTotal] = useState(0.0);
    const [serviceCharge, setServiceCharge] = useState(0.0);
    const [taxes, setTaxes] = useState(0.0);
    const [discount, setDiscount] = useState(0.0);

    const calculateOrder = async (currentUpdates) => {
        if (eventDetails && currentUpdates.length > 0) {
            try {
                const payload = {
                    eventPageGid: eventDetails.gid,
                    promoCode: null,
                    tip: null,
                    tipType: "AMOUNT",
                    eventPageOrderLineItems: currentUpdates.map(u => {
                        const productGid = u.productGid || eventPageProduct?.gid;
                        if (u.itemGid) {
                            return {
                                productGid,
                                customItemGid: u.customItemGid,
                                itemGid: null,
                                name: u.name,
                                price: u.price,
                                quantity: u.quantity,
                                lineItemModifiers: [],
                                lineItemItems: [{
                                    itemGid: u.itemGid,
                                    name: u.name,
                                    price: u.price,
                                    quantity: 1,
                                    lineItemModifiers: u.lineItemModifiers || [],
                                    lineItemItems: u.lineItemItems || [],
                                    lineItemDiscounts: u.lineItemDiscounts || [],
                                }],
                                seatLabel: u.seatLabel
                            };
                        }

                        return {
                            productGid,
                            customItemGid: u.customItemGid,
                            itemGid: u.itemGid,
                            name: u.name,
                            price: u.price,
                            quantity: u.quantity,
                            lineItemModifiers: u.lineItemModifiers || [],
                            lineItemDiscounts: u.lineItemDiscounts || [],
                            seatLabel: u.seatLabel,
                            seats: u.seats || (u.seatLabel ? [u.seatLabel] : []),
                            lineItemItems: u.eventPageTicketTypeGid ? [{
                                quantity: 1,
                                ticketTypeGid: u.eventPageTicketTypeGid
                            }] : (u.lineItemItems || [])
                        };
                    }),
                    eventPageOrderDiscounts: [],
                };

                const value = await eventsPageSplitUpApi(payload);
                if (value) {
                    setSubtotal(value.subTotal ? AmountDivideByHundred(value.subTotal) : 0);
                    setServiceCharge(value.serviceFee ? AmountDivideByHundred(value.serviceFee) : 0);
                    setTaxes(value.tax ? AmountDivideByHundred(value.tax) : 0);
                    setDiscount(value.totalDiscount ? AmountDivideByHundred(value.totalDiscount) : 0);
                    setTotal(value.netPayable ? AmountDivideByHundred(value.netPayable) : 0);
                }
            } catch (error) {
                console.error("Calculate order error:", error);
            }
        } else {
            setSubtotal(0);
            setServiceCharge(0);
            setTaxes(0);
            setDiscount(0);
            setTotal(0);
        }
    };

    const getTotalAmount = () => {
        return updates.reduce((sum, item) => sum + item.totalPrice, 0);
    };

    const getEventTimeRange = (dateOption) => {
        if (!dateOption || dateOption.eventStartTime === undefined || dateOption.eventStartTime === null) return "";
        const startTime = getOperatingTime(dateOption.eventStartTime);
        if (dateOption.eventEndTime === undefined || dateOption.eventEndTime === null || dateOption.eventEndTime === dateOption.eventStartTime) {
            return startTime;
        }
        return `${startTime} - ${getOperatingTime(dateOption.eventEndTime)}`;
    };

    useEffect(() => {
        if (eventDetails.isSeatedEvent && eventPageProduct?.eventPageProductType === 'TICKET') {
            setShowButton(false);
        } else {
            setShowButton(true);
        }
    }, [eventPageProduct, eventDetails.isSeatedEvent, setShowButton]);

    const toggleModifier = (item) => {
        const key = item.lineItemKey || (item.customItemGid || item.itemGid);
        if (openModifierGid === key) {
            setOpenModifierGid(null);
        } else {
            setOpenModifierGid(key);
            setOpenItemsGid(null);
        }
    };

    const toggleItems = (item) => {
        const key = item.lineItemKey || (item.customItemGid || item.itemGid);
        if (openItemsGid === key) {
            setOpenItemsGid(null);
        } else {
            setOpenItemsGid(key);
            setOpenModifierGid(null);
        }
    };

    const recalculateTotalPrice = (u) => {
        const modifiersTotal = u.lineItemModifiers?.reduce((s, m) => s + (m.price * m.quantity), 0) || 0;
        const itemsTotal = u.lineItemItems?.reduce((s, i) => {
            const nestedModifiersTotal = i.lineItemModifiers?.reduce((sm, m) => sm + (m.price * m.quantity), 0) || 0;
            return s + (i.price * i.quantity) + nestedModifiersTotal;
        }, 0) || 0;
        return (u.price + modifiersTotal + itemsTotal) * u.quantity;
    };

    const addModifier = (item, modifier, modifierGroup) => {
        setUpdates(prev => {
            return prev.map(u => {
                const isMatch = u.lineItemKey === item.lineItemKey;
                
                if (isMatch) {
                    const existingModifier = u.lineItemModifiers?.find(m => m.modifierGid === modifier.gid);
                    
                    // 1. Check maxStackableQuantity for the individual modifier
                    if (modifier.maxStackableQuantity && existingModifier && existingModifier.quantity >= modifier.maxStackableQuantity) {
                        return u;
                    }

                    // 2. Check maxAllowed for the entire group
                    if (modifierGroup?.maxAllowed) {
                        const currentGroupTotal = u.lineItemModifiers?.reduce((sum, m) => {
                            if (modifierGroup.modifiers.some(gm => gm.gid === m.modifierGid)) {
                                return sum + m.quantity;
                            }
                            return sum;
                        }, 0) || 0;

                        if (currentGroupTotal >= modifierGroup.maxAllowed) {
                            return u;
                        }
                    }

                    let newModifiers;
                    if (existingModifier) {
                        newModifiers = u.lineItemModifiers.map(m =>
                            m.modifierGid === modifier.gid ? { ...m, quantity: m.quantity + 1 } : m
                        );
                    } else {
                        newModifiers = [...(u.lineItemModifiers || []), {
                            modifierGid: modifier.gid,
                            name: modifier.name,
                            price: modifier.price,
                            quantity: 1
                        }];
                    }

                    const updatedU = { ...u, lineItemModifiers: newModifiers };
                    return { ...updatedU, totalPrice: recalculateTotalPrice(updatedU) };
                }
                return u;
            });
        });
    };

    const removeModifier = (item, modifier) => {
        setUpdates(prev => {
            return prev.map(u => {
                const isMatch = u.lineItemKey === item.lineItemKey;

                if (isMatch) {
                    const newModifiers = u.lineItemModifiers?.map(m =>
                        m.modifierGid === modifier.gid ? { ...m, quantity: Math.max(0, m.quantity - 1) } : m
                    ).filter(m => m.quantity > 0);

                    const updatedU = { ...u, lineItemModifiers: newModifiers };
                    return { ...updatedU, totalPrice: recalculateTotalPrice(updatedU) };
                }
                return u;
            });
        });
    };

    const addNestedItem = (parentItem, nestedItem) => {
        setUpdates(prev => {
            return prev.map(u => {
                const isMatch = u.lineItemKey === parentItem.lineItemKey;

                if (isMatch) {
                    const existingItem = u.lineItemItems?.find(i => i.itemGid === nestedItem.gid);
                    
                    // Check individual item limit (maxStackableQuantity or onlineStock)
                    if (nestedItem.maxStackableQuantity && existingItem && existingItem.quantity >= nestedItem.maxStackableQuantity) {
                        return u;
                    }
                    if (nestedItem.enableStock && existingItem) {
                        if ((nestedItem.onlineStock !== undefined && existingItem.quantity >= nestedItem.onlineStock) ||
                            (nestedItem.stockQuantity !== undefined && existingItem.quantity >= nestedItem.stockQuantity)) {
                            return u;
                        }
                    }

                    let newItems;
                    if (existingItem) {
                        newItems = u.lineItemItems.map(i =>
                            i.itemGid === nestedItem.gid ? { ...i, quantity: i.quantity + 1 } : i
                        );
                    } else {
                        newItems = [...(u.lineItemItems || []), {
                            itemGid: nestedItem.gid,
                            name: nestedItem.displayName || nestedItem.inputFieldLabel,
                            price: nestedItem.price || nestedItem.amount || 0,
                            quantity: 1,
                            lineItemModifiers: [],
                            lineItemDiscounts: nestedItem.coupon ? [{
                                couponGid: nestedItem.coupon.gid,
                                name: nestedItem.coupon.name || "Item Discount",
                                amountOff: nestedItem.coupon.amountOff || 0,
                                percentageOff: nestedItem.coupon.percentageOff || 0,
                            }] : [],
                        }];
                    }
                    const updatedU = { ...u, lineItemItems: newItems };
                    return { ...updatedU, totalPrice: recalculateTotalPrice(updatedU) };
                }
                return u;
            });
        });
    };

    const removeNestedItem = (parentItem, nestedItemInLine) => {
        setUpdates(prev => {
            return prev.map(u => {
                const isMatch = u.lineItemKey === parentItem.lineItemKey;

                if (isMatch) {
                    const newItems = u.lineItemItems?.map(i =>
                        i.itemGid === nestedItemInLine.itemGid ? { ...i, quantity: Math.max(0, i.quantity - 1) } : i
                    ).filter(i => i.quantity > 0);

                    const updatedU = { ...u, lineItemItems: newItems };
                    return { ...updatedU, totalPrice: recalculateTotalPrice(updatedU) };
                }
                return u;
            });
        });
    };

    const addNestedItemModifier = (parentItem, nestedItem, modifier, modifierGroup) => {
        setUpdates(prev => {
            return prev.map(u => {
                const isMatch = u.lineItemKey === parentItem.lineItemKey;
                if (!isMatch) return u;

                const newItems = u.lineItemItems?.map(lii => {
                    if (lii.itemGid === nestedItem.itemGid) {
                        const existingModifier = lii.lineItemModifiers?.find(m => m.modifierGid === modifier.gid);

                        // 1. Check maxStackableQuantity for individual nested modifier
                        if (modifier.maxStackableQuantity && existingModifier && existingModifier.quantity >= modifier.maxStackableQuantity) {
                            return lii;
                        }

                        // 2. Check maxAllowed for the nested group
                        if (modifierGroup?.maxAllowed) {
                            const currentGroupTotal = lii.lineItemModifiers?.reduce((sum, m) => {
                                if (modifierGroup.modifiers.some(gm => gm.gid === m.modifierGid)) {
                                    return sum + m.quantity;
                                }
                                return sum;
                            }, 0) || 0;

                            if (currentGroupTotal >= modifierGroup.maxAllowed) {
                                return lii;
                            }
                        }

                        let newModifiers;
                        if (existingModifier) {
                            newModifiers = lii.lineItemModifiers.map(m =>
                                m.modifierGid === modifier.gid ? { ...m, quantity: m.quantity + 1 } : m
                            );
                        } else {
                            newModifiers = [...(lii.lineItemModifiers || []), {
                                modifierGid: modifier.gid,
                                name: modifier.name,
                                price: modifier.price,
                                quantity: 1
                            }];
                        }
                        return { ...lii, lineItemModifiers: newModifiers };
                    }
                    return lii;
                });

                const updatedU = { ...u, lineItemItems: newItems };
                return { ...updatedU, totalPrice: recalculateTotalPrice(updatedU) };
            });
        });
    };

    const removeNestedItemModifier = (parentItem, nestedItemInLine, modifier) => {
        setUpdates(prev => {
            return prev.map(u => {
                const isMatch = u.lineItemKey === parentItem.lineItemKey;

                if (isMatch) {
                    const newItems = u.lineItemItems.map(i => {
                        if (i.itemGid === nestedItemInLine.itemGid) {
                            const newMods = i.lineItemModifiers?.map(m =>
                                m.modifierGid === modifier.gid ? { ...m, quantity: Math.max(0, m.quantity - 1) } : m
                            ).filter(m => m.quantity > 0);
                            return { ...i, lineItemModifiers: newMods };
                        }
                        return i;
                    });
                    const updatedU = { ...u, lineItemItems: newItems };
                    return { ...updatedU, totalPrice: recalculateTotalPrice(updatedU) };
                }
                return u;
            });
        });
    };

    useEffect(() => {
        if (updates.length > 0) {
            setOrderItems(prev => {
                const newOrderItems = [...prev];
                const currentItem = {
                    ...(newOrderItems[orderItemIndex] || {
                        productGid: eventPageProduct?.gid,
                        title: eventPageProduct?.eventPageProductName,
                        eventPageProductType: eventPageProduct?.eventPageProductType
                    })
                };
                currentItem.lineItems = updates.map(u => ({
                    id: u.seatLabel,
                    name: u.name,
                    price: u.price,
                    customItemAmount: u.customItemAmount != null ? u.customItemAmount : null,
                    quantity: u.quantity,
                    totalPrice: u.totalPrice,
                    lineItemModifiers: u.lineItemModifiers || [],
                    lineItemItems: u.lineItemItems || [],
                    lineItemDiscounts: u.lineItemDiscounts || [],
                    seatLabel: u.seatLabel,
                    seats: u.seats || [],
                    productGid: u.productGid || eventPageProduct?.gid,
                    customItemGid: u.customItemGid,
                    itemGid: u.itemGid,
                    eventPageTicketTypeGid: u.eventPageTicketTypeGid || null,
                    listedItem: u.listedItem,
                    duplicateIndex: u.duplicateIndex,
                    lineItemKey: u.lineItemKey,
                }));
                currentItem.totalAmount = getTotalAmount();
                currentItem.totalCount = updates.reduce((sum, u) => sum + u.quantity, 0);
                newOrderItems[orderItemIndex] = currentItem;
                return newOrderItems;
            });
            calculateOrder(updates);
        } else {
            setOrderItems(prev => {
                const newOrderItems = [...prev];
                if (newOrderItems[orderItemIndex]) {
                    newOrderItems[orderItemIndex].lineItems = [];
                    newOrderItems[orderItemIndex].totalAmount = 0;
                    newOrderItems[orderItemIndex].totalCount = 0;
                }
                return newOrderItems;
            });
            setTotal(0);
            setSubtotal(0);
            setServiceCharge(0);
            setTaxes(0);
        }
    }, [updates, orderItemIndex, eventPageProduct, setOrderItems]);

    const updateQuantity = (customItem, item, modifier, seatLabel, change, price, ticketType) => {
        const isTicket = eventPageProduct?.eventPageProductType === "TICKET";

        if (change === 1) {
            setUpdates(prev => {
                // For tickets, we want each as a separate line item
                if (isTicket) {
                    if (seatLabel && prev.some(u => u.seatLabel === seatLabel)) return prev;

                    return [
                        ...prev,
                        {
                            productGid: eventPageProduct?.gid,
                            customItemGid: customItem?.gid,
                            itemGid: item?.gid,
                            eventPageTicketTypeGid: ticketType?.gid || null,
                            name: ticketType
                                ? `${customItem?.inputFieldLabel} - ${ticketType.name}`
                                : (customItem?.inputFieldLabel || item?.displayName || seatLabel),
                            price: price,
                            customItemAmount: customItem?.amount || 0,
                            quantity: 1,
                            totalPrice: price,
                            lineItemModifiers: [],
                            lineItemDiscounts: item?.coupon ? [{
                                couponGid: item.coupon.gid,
                                name: item.coupon.name || "Item Discount",
                                amountOff: item.coupon.amountOff || 0,
                                percentageOff: item.coupon.percentageOff || 0,
                            }] : [],
                            seats: seatLabel ? [seatLabel] : [],
                            seatLabel: seatLabel,
                            modifierGroups: customItem?.modifierGroups || [],
                            lineItemKey: seatLabel || `ticket-${Date.now()}-${Math.random()}`
                        }
                    ];
                }

                // Default grouping logic for non-seated items
                const existingIndex = prev.findIndex(u => u.customItemGid === customItem?.gid && u.itemGid === item?.gid);
                if (existingIndex !== -1) {
                    const newUpdates = [...prev];
                    const existing = newUpdates[existingIndex];

                    const newQuantity = existing.quantity + 1;
                    const modifiersTotal = existing.lineItemModifiers?.reduce((s, m) => s + (m.price * m.quantity), 0) || 0;

                    const updatedItem = {
                        ...existing,
                        quantity: newQuantity
                    };

                    newUpdates[existingIndex] = {
                        ...updatedItem,
                        totalPrice: recalculateTotalPrice(updatedItem),
                    };
                    return newUpdates;
                }

                return [
                    ...prev,
                    {
                        productGid: eventPageProduct?.gid,
                        customItemGid: customItem?.gid,
                        itemGid: item?.gid,
                        name: customItem?.inputFieldLabel || item?.displayName || seatLabel,
                        price: price,
                        quantity: 1,
                        totalPrice: price,
                        lineItemModifiers: [],
                        lineItemDiscounts: item?.coupon ? [{
                            couponGid: item.coupon.gid,
                            name: item.coupon.name || "Item Discount",
                            amountOff: item.coupon.amountOff || 0,
                            percentageOff: item.coupon.percentageOff || 0,
                        }] : [],
                        seats: seatLabel ? [seatLabel] : [],
                        seatLabel: seatLabel,
                        modifierGroups: customItem?.modifierGroups || [],
                        lineItemKey: seatLabel || `${customItem?.gid || item?.gid}`
                    }
                ];
            });
        } else if (change === -1) {
            setUpdates(prev => {
                const existingIndex = isTicket && seatLabel 
                    ? prev.findIndex(u => u.seatLabel === seatLabel)
                    : prev.findIndex(u => u.customItemGid === customItem?.gid && u.itemGid === item?.gid);
                
                if (existingIndex !== -1) {
                    const newUpdates = [...prev];
                    const existing = newUpdates[existingIndex];
                    if (existing.quantity > 1) {
                        const newQuantity = existing.quantity - 1;
                        const updatedItem = {
                            ...existing,
                            quantity: newQuantity
                        };
                        newUpdates[existingIndex] = {
                            ...updatedItem,
                            totalPrice: recalculateTotalPrice(updatedItem),
                        };
                    } else {
                        newUpdates.splice(existingIndex, 1);
                    }
                    return newUpdates;
                }
                return prev;
            });
        }
    };

    const scrollToId = (id) => {
        setTimeout(() => {
            const element = document.getElementById(id);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 100);
    };

    const checkModifierError = (item) => {
        const productDef = eventPageProduct?.customItems?.find(ci => ci.gid === item.customItemGid) ||
                           eventPageProduct?.items?.find(i => i.gid === item.itemGid);
        
        if (productDef?.modifierGroups) {
            for (const mg of productDef.modifierGroups) {
                if (mg.minRequired > 0) {
                    const selectedModifiers = item.lineItemModifiers?.filter(lm => 
                        mg.modifiers.some(m => m.gid === lm.modifierGid)
                    ) || [];
                    const selectedCount = selectedModifiers.reduce((s, m) => s + m.quantity, 0);
                    if (selectedCount < mg.minRequired) {
                        return true;
                    }
                }
            }
        }
        return false;
    };

    const checkItemsModifierError = (item) => {
        const productDef = eventPageProduct?.customItems?.find(ci => ci.gid === item.customItemGid) ||
                           eventPageProduct?.items?.find(i => i.gid === item.itemGid);
        
        if (item.lineItemItems?.length > 0) {
            for (const lii of item.lineItemItems) {
                const nestedItemDef = productDef?.items?.find(ni => ni.gid === lii.itemGid);
                if (nestedItemDef?.modifierGroups) {
                    for (const nmg of nestedItemDef.modifierGroups) {
                        if (nmg.minRequired > 0) {
                            const selectedModifiers = lii.lineItemModifiers?.filter(lm => 
                                nmg.modifiers.some(m => m.gid === lm.modifierGid)
                            ) || [];
                            const selectedCount = selectedModifiers.reduce((s, m) => s + m.quantity, 0);
                            if (selectedCount < nmg.minRequired) {
                                return true;
                            }
                        }
                    }
                }
            }
        }
        return false;
    };

    const checkGroupError = (item, mg) => {
        if (mg.minRequired > 0) {
            const selectedModifiers = item.lineItemModifiers?.filter(lm => 
                mg.modifiers.some(m => m.gid === lm.modifierGid)
            ) || [];
            const selectedCount = selectedModifiers.reduce((s, m) => s + m.quantity, 0);
            return selectedCount < mg.minRequired;
        }
        return false;
    };

    const checkNestedGroupError = (lii, nmg) => {
        if (nmg.minRequired > 0) {
            const selectedModifiers = lii.lineItemModifiers?.filter(lm => 
                nmg.modifiers.some(m => m.gid === lm.modifierGid)
            ) || [];
            const selectedCount = selectedModifiers.reduce((s, m) => s + m.quantity, 0);
            return selectedCount < nmg.minRequired;
        }
        return false;
    };

    const handleNextClick = () => {
        setValidationTriggered(true);
        // 1. Validation for mandatory items (isOptional: false)
        if (eventPageProduct?.customItems) {
            const mandatoryMissing = eventPageProduct.customItems.filter(ci => 
                !ci.isOptional && !updates.some(u => u.customItemGid === ci.gid)
            );
            if (mandatoryMissing.length > 0) {
                notify(`Please add ${mandatoryMissing[0].inputFieldLabel} to proceed`, "error");
                if (mobileView) {
                    scrollToId("choose-seats-section");
                }
                return;
            }
        }

        // 2. Validation for modifier groups (minRequired)
        for (const update of updates) {
            const productDef = eventPageProduct?.customItems?.find(ci => ci.gid === update.customItemGid) ||
                               eventPageProduct?.items?.find(i => i.gid === update.itemGid);
            
            if (productDef?.modifierGroups) {
                for (const mg of productDef.modifierGroups) {
                    if (mg.minRequired > 0) {
                        const selectedModifiers = update.lineItemModifiers?.filter(lm => 
                            mg.modifiers.some(m => m.gid === lm.modifierGid)
                        ) || [];
                        const selectedCount = selectedModifiers.reduce((s, m) => s + m.quantity, 0);
                        
                        if (selectedCount < mg.minRequired) {
                            notify(`Please select at least ${mg.minRequired} for ${mg.name} in ${update.name}`, "error");
                            const itemKey = update.lineItemKey || (update.customItemGid || update.itemGid);
                            setOpenModifierGid(itemKey);
                            setOpenItemsGid(null);
                            if (mobileView) {
                                scrollToId(`ticket-item-${itemKey}`);
                            }
                            return;
                        }
                    }
                }
            }

            // Nested items modifier validation
            if (update.lineItemItems?.length > 0) {
                for (const lii of update.lineItemItems) {
                    const nestedItemDef = productDef?.items?.find(ni => ni.gid === lii.itemGid);
                    if (nestedItemDef?.modifierGroups) {
                        for (const nmg of nestedItemDef.modifierGroups) {
                            if (nmg.minRequired > 0) {
                                const selectedModifiers = lii.lineItemModifiers?.filter(lm => 
                                    nmg.modifiers.some(m => m.gid === lm.modifierGid)
                                ) || [];
                                const selectedCount = selectedModifiers.reduce((s, m) => s + m.quantity, 0);
                                
                                if (selectedCount < nmg.minRequired) {
                                    notify(`Please select at least ${nmg.minRequired} for ${nmg.name} in ${lii.name}`, "error");
                                    const itemKey = update.lineItemKey || (update.customItemGid || update.itemGid);
                                    setOpenItemsGid(itemKey);
                                    setOpenModifierGid(null);
                                    if (mobileView) {
                                        scrollToId(`ticket-item-${itemKey}`);
                                    }
                                    return;
                                }
                            }
                        }
                    }
                }
            }
        }

        handleNext();
    };

    return (
        <div className="figma-add-products-container">
            {/* Left Content: Dates and Seat Map */}
            <div className="figma-content-left">
                {/* Event Info & Date Selection Card */}
                <div className="figma-selection-card" style={{ background: '#fff', padding: mobileView ? '16px 16px 16px 16px' : '32px', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)', overflow: 'visible', marginTop: mobileView ? '0px' : '0' }}>
                    <div style={{ marginBottom: '24px', overflow: 'visible' }}>
                        <h2 className="figma-event-title-address">
                            {eventDetails?.title} @ {eventDetails?.address?.street || eventDetails?.eventPageAddress}, {eventDetails?.address?.city}, {eventDetails?.address?.state}, USA, {eventDetails?.address?.postalCode}
                        </h2>

                        {(() => {
                            const activeDate = eventDetails?.eventPageDateTimes?.find(d => d.gid === selectedDate?.gid);
                            return (
                                <div className="figma-selected-date-info">
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                                        <path d="M5.3335 1.33301V3.99967" stroke={eventDetails?.primaryColor || '#155DFC'} strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M10.6665 1.33301V3.99967" stroke={eventDetails?.primaryColor || '#155DFC'} strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M12.6667 2.66699H3.33333C2.59695 2.66699 2 3.26395 2 4.00033V13.3337C2 14.07 2.59695 14.667 3.33333 14.667H12.6667C13.403 14.667 14 14.07 14 13.3337V4.00033C14 3.26395 13.403 2.66699 12.6667 2.66699Z" stroke={eventDetails?.primaryColor || '#155DFC'} strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M2 6.66699H14" stroke={eventDetails?.primaryColor || '#155DFC'} strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    <span style={{ fontWeight: '600', color: '#1f2937', flexShrink: 0 }}>Selected Event Date:</span>
                                    <span style={{ color: '#4b5563' }}>
                                        {activeDate ? `${moment.utc(activeDate.eventDate).format('MMM DD, YYYY')}, ${getEventTimeRange(activeDate)}` : 'Select a date'}
                                    </span>
                                </div>
                            );
                        })()}
                    </div>

                    <div style={{ position: 'relative', width: '100%', overflow: 'visible' }}>
                        {/* Left Scroll Indicator & Button (Mobile View only) */}
                        {mobileView && eventDetails?.eventPageDateTimes?.length > 1 && canScrollLeft && (
                            <>
                                <div style={{
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: '40px',
                                    background: 'linear-gradient(to right, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0))',
                                    pointerEvents: 'none',
                                    zIndex: 5
                                }} />
                                <button
                                    onClick={scrollLeftFn}
                                    style={{
                                        position: 'absolute',
                                        left: '4px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        zIndex: 10,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        background: '#ffffff',
                                        border: '1px solid #e2e8f0',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                        cursor: 'pointer',
                                        color: eventDetails?.primaryColor || '#2563eb',
                                        padding: 0
                                    }}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="15 18 9 12 15 6"></polyline>
                                    </svg>
                                </button>
                            </>
                        )}

                        <div
                            ref={scrollRef}
                            className="figma-dates-horizontal-scroll"
                            style={{ display: 'flex', gap: '16px', overflowX: 'auto', paddingBottom: '8px', flexWrap: 'nowrap', WebkitOverflowScrolling: 'touch', width: '100%' }}
                        >
                            {eventDetails?.eventPageDateTimes?.map((dateTime, idx) => (
                                <div
                                    key={idx}
                                    className={`figma-date-card-modern ${selectedDate?.gid === dateTime.gid ? 'selected' : ''} ${dateTime.disabled ? 'disabled' : ''}`}
                                    style={{
                                        ...(selectedDate?.gid === dateTime.gid ? {
                                            borderColor: eventDetails?.primaryColor || '#2563eb',
                                            background: `${eventDetails?.primaryColor || '#2563eb'}0a`
                                        } : {}),
                                        ...(dateTime.disabled ? {
                                            opacity: 0.6,
                                            filter: 'grayscale(1)',
                                            cursor: 'not-allowed',
                                            pointerEvents: 'none'
                                        } : {})
                                    }}
                                    onClick={() => {
                                        if (!dateTime.disabled && selectedDate?.gid !== dateTime.gid) {
                                            const seats = sessionStorage.getItem('seatsio') ? JSON.parse(sessionStorage.getItem('seatsio')) : undefined;
                                            if (seats?.holdToken) {
                                                expireHoldToken(seats.holdToken);
                                            }
                                            sessionStorage.removeItem('seatsio');
                                            setSelectedDate(dateTime);
                                            setUpdates([]);
                                            setOrderItems([]);
                                        }
                                    }}
                                >
                                        <div className="figma-date-text">
                                            {moment.utc(dateTime.eventDate).format('MMM DD, YYYY')}
                                        </div>
                                        <div className="figma-time-text">
                                            {getEventTimeRange(dateTime)}
                                        </div>
                                        {dateTime.disabled && (
                                            <div
                                                className="figma-selected-badge"
                                                style={{
                                                    position: 'static',
                                                    marginTop: '12px',
                                                    display: 'inline-flex',
                                                    background: '#ef4444'
                                                }}
                                            >
                                                Sold Out
                                            </div>
                                        )}
                                        {!dateTime.disabled && selectedDate?.gid === dateTime.gid && (
                                            <div
                                                className="figma-selected-badge"
                                                style={{
                                                    position: 'static',
                                                    marginTop: '12px',
                                                    display: 'inline-flex',
                                                    background: eventDetails?.primaryColor || '#2563eb'
                                                }}
                                            >
                                                ✓ Selected
                                            </div>
                                        )}
                                </div>
                            ))}
                        </div>

                        {/* Right Scroll Indicator & Button (Mobile View only) */}
                        {mobileView && eventDetails?.eventPageDateTimes?.length > 1 && canScrollRight && (
                            <>
                                <div style={{
                                    position: 'absolute',
                                    right: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: '40px',
                                    background: 'linear-gradient(to left, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0))',
                                    pointerEvents: 'none',
                                    zIndex: 5
                                }} />
                                <button
                                    onClick={scrollRightFn}
                                    style={{
                                        position: 'absolute',
                                        right: '4px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        zIndex: 10,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        background: '#ffffff',
                                        border: '1px solid #e2e8f0',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                        cursor: 'pointer',
                                        color: eventDetails?.primaryColor || '#2563eb',
                                        padding: 0
                                    }}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="9 18 15 12 9 6"></polyline>
                                    </svg>
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Seat Map Section */}
                {eventDetails.isSeatedEvent && eventPageProduct?.eventPageProductType === 'TICKET' && (
                    <div id="choose-seats-section" className="figma-selection-card" style={{ background: '#fff', padding: '24px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>Choose Your Seats</h3>
                            <div style={{ display: 'flex', gap: '16px', fontSize: '13px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#05a588' }}></div>
                                    <span>Available</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#e2e8f0' }}></div>
                                    <span>Occupied</span>
                                </div>
                            </div>
                        </div>
                        <div className="figma-seat-map" style={{ minHeight: '500px', display: 'flex', flexDirection: 'column' }}>
                            <ChooseSeats
                                eventDetails={eventDetails}
                                eventPageProduct={eventPageProduct}
                                updateQuantity={updateQuantity}
                                setFullScreen={setFullScreen}
                                mobileView={mobileView}
                                selectedDateGid={selectedDate?.gid}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Right Sidebar: Ticket Details & Summary */}
            <div className="figma-content-right">
                <div className="figma-sidebar-card" style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', overflow: 'visible', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: '24px 24px 12px 24px', flexShrink: 0 }}>
                        <h3 className="figma-sidebar-title" style={{ fontSize: '17px', fontWeight: '700', color: '#1e293b', margin: 0, letterSpacing: '-0.01em', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10 2H14V6L13 7L14 8V14H10M10 2H6V14H10M10 2V14" stroke={eventDetails?.primaryColor || '#155DFC'} strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M2 6H6M2 10H6" stroke={eventDetails?.primaryColor || '#155DFC'} strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Ticket Details
                        </h3>
                    </div>

                    <div className="figma-ticket-list" style={{ padding: '24px' }}>
                        {updates.length === 0 ? (
                            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#94a3b8' }}>
                                <div style={{ fontSize: '32px', marginBottom: '12px' }}>🎟️</div>
                                <p style={{ fontSize: '14px' }}>No seats selected yet. Please select your seats.</p>
                            </div>
                        ) : (
                            updates.map((item, idx) => (
                                <div key={idx} id={`ticket-item-${item.lineItemKey || (item.customItemGid || item.itemGid)}`} className="figma-ticket-item" style={{ marginBottom: '24px', borderBottom: '1px solid #f1f5f9', paddingBottom: '20px' }}>
                                    {(() => {
                                        const nameParts = item?.name ? String(item.name).split(' - ') : [];
                                        const isPriceStr = (str) => typeof str === 'string' && /^\s*[\$€£₹]?\s*\d+([.,]\d+)?\s*(USD|EUR|GBP|INR|CAD|AUD)?\s*$/i.test(str.trim());
                                        const nonPriceParts = nameParts.filter(p => !isPriceStr(p));
                                        const categoryName = nonPriceParts.length > 1 ? nonPriceParts[0] : item?.name;
                                        const ticketTypeName = nonPriceParts.length > 1 ? nonPriceParts.slice(1).join(' - ') : null;

                                        const disc = item.lineItemDiscounts?.length > 0 ? item.lineItemDiscounts[0] : null;
                                        const unitDiscount = disc
                                            ? (disc.percentageOff
                                                ? Math.round(item.price * (disc.percentageOff / 100))
                                                : Math.abs(disc.amountOff || 0))
                                            : 0;
                                        const parentDiscountAmount = unitDiscount * item.quantity;
                                        const parentGrossPrice = item.price * item.quantity;
                                        const parentNetPrice = parentGrossPrice - parentDiscountAmount;
                                        const showPrice = item.customItemAmount != null
                                            ? item.customItemAmount
                                            : (ticketTypeName ? parentGrossPrice : parentNetPrice);

                                        return (
                                            <div style={{ marginBottom: '8px' }}>
                                                {/* Category Name + base amount (top row) */}
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2px' }}>
                                                    <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '16px', letterSpacing: '-0.01em' }}>
                                                        {categoryName}
                                                    </div>
                                                    {/* Show base customItem amount if available, else the net price */}
                                                    {showPrice > 0 && (
                                                    <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '14px', marginLeft: '16px' }}>
                                                        ${AmountDivideByHundred(showPrice)}
                                                    </div>
                                                    )}
                                                </div>

                                                {/* Ticket Type + its price (sub-row, only when type exists) */}
                                                {ticketTypeName && (
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                                        <div style={{ fontSize: '13px', color: '#475569', fontWeight: '500' }}>
                                                            {ticketTypeName}
                                                        </div>
                                                        <div style={{ textAlign: 'right', marginLeft: '16px' }}>
                                                            {parentNetPrice > 0 && (
                                                            <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '13px' }}>
                                                                ${AmountDivideByHundred(parentNetPrice)}
                                                            </div>
                                                            )}
                                                            {parentDiscountAmount >= 1 && (
                                                                <div style={{ fontSize: '10px', color: '#ef4444', marginTop: '2px' }}>
                                                                    - Discount: ${AmountDivideByHundred(parentDiscountAmount)}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Seat & Qty info */}
                                                <div style={{ fontSize: '12px', color: '#64748b' }}>
                                                    Qty: {item.quantity}{item.seats?.length > 0 ? ` • Seats: ${item.seats.join(', ')}` : ''}
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* Modifiers Display in Sidebar */}
                                    {item.lineItemModifiers?.length > 0 && (
                                        <div style={{ fontSize: "11px", color: "#64748b", paddingLeft: "8px", borderLeft: "1px solid #e2e8f0", marginLeft: "4px", marginTop: "8px" }}>
                                            {item.lineItemModifiers.map((m, mIdx) => (
                                                <div key={mIdx} style={{ display: "flex", justifyContent: "space-between" }}>
                                                    <span>+ {m.name} x {m.quantity * item.quantity}</span>
                                                    {m.price > 0 && (
                                                    <span>${AmountDivideByHundred(m.price * m.quantity * item.quantity)}</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Nested Items and their Modifiers Display */}
                                    {item.lineItemItems?.length > 0 && (
                                        <div style={{ fontSize: "11px", color: "#64748b", paddingLeft: "8px", borderLeft: "1px solid #e2e8f0", marginLeft: "4px", marginTop: "4px" }}>
                                            {item.lineItemItems.map((lii, liiIdx) => (
                                                    <div key={liiIdx} style={{ marginBottom: "4px" }}>
                                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: 'flex-start' }}>
                                                            <span>• {lii.name} x {lii.quantity * item.quantity}</span>
                                                            <div style={{ textAlign: 'right' }}>
                                                                 {(() => {
                                                                    const unitDiscount = lii.lineItemDiscounts?.length > 0 ? lii.lineItemDiscounts[0] : null;
                                                                    const unitDiscountAmount = unitDiscount
                                                                        ? (unitDiscount.percentageOff
                                                                            ? Math.round(lii.price * (unitDiscount.percentageOff / 100))
                                                                            : Math.abs(unitDiscount.amountOff || 0))
                                                                        : 0;
                                                                    const itemDiscountAmount = unitDiscountAmount * lii.quantity * item.quantity;
                                                                    const itemTotal = (lii.price + (lii.lineItemModifiers?.reduce((s, m) => s + m.price * m.quantity, 0) || 0)) * lii.quantity * item.quantity;
                                                                    const finalPrice = itemTotal - itemDiscountAmount;

                                                                    return itemDiscountAmount >= 1 ? (
                                                                        <>
                                                                            <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '13px' }}>
                                                                                <span style={{ textDecoration: 'line-through', color: '#94a3b8', fontSize: '10px', marginRight: '4px' }}>
                                                                                    ${AmountDivideByHundred(itemTotal)}
                                                                                </span>
                                                                                <span>${AmountDivideByHundred(finalPrice)}</span>
                                                                            </div>
                                                                            <div style={{ fontSize: '9px', color: '#ef4444' }}>
                                                                                - ${AmountDivideByHundred(itemDiscountAmount)}
                                                                            </div>
                                                                        </>
                                                                    ) : (
                                                                        <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '13px' }}>${AmountDivideByHundred(itemTotal)}</div>
                                                                    );
                                                                })()}
                                                            </div>
                                                        </div>
                                                    {lii.lineItemModifiers?.length > 0 && (
                                                        <div style={{ paddingLeft: "8px", opacity: 0.8 }}>
                                                            {lii.lineItemModifiers.map((m, mIdx) => (
                                                                <div key={mIdx} style={{ display: "flex", justifyContent: "space-between" }}>
                                                                    <span>&nbsp;&nbsp;+ {m.name} x {m.quantity}</span>
                                                                    <span>${AmountDivideByHundred(m.price * m.quantity)}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Customize Menu */}
                                    {(() => {
                                        const productDef = eventPageProduct?.customItems?.find(ci => ci.gid === item.customItemGid) ||
                                            eventPageProduct?.items?.find(i => i.gid === item.itemGid);

                                        const hasModifiers = productDef?.modifierGroups?.length > 0;
                                        const hasItems = productDef?.items?.length > 0;
                                        const itemKey = item.lineItemKey || (item.customItemGid || item.itemGid);
                                        const isModifierOpen = openModifierGid === itemKey;
                                        const isItemsOpen = openItemsGid === itemKey;

                                        if (!hasModifiers && !hasItems) return null;

                                        return (
                                            <div style={{ marginTop: '12px' }}>
                                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                    {hasModifiers && (
                                                        <button
                                                            className="drop-down-menu"
                                                            onClick={() => toggleModifier(item)}
                                                            style={{
                                                                flex: 1,
                                                                background: validationTriggered && checkModifierError(item) ? '#fef2f2' : `${eventDetails?.primaryColor || '#2563eb'}0a`,
                                                                borderColor: validationTriggered && checkModifierError(item) ? '#ef4444' : `${eventDetails?.primaryColor || '#2563eb'}30`,
                                                                color: validationTriggered && checkModifierError(item) ? '#ef4444' : eventDetails?.primaryColor || '#2563eb',
                                                                marginTop: '8px',
                                                                minWidth: '120px'
                                                            }}
                                                        >
                                                            <span>{isModifierOpen ? 'Close Modifiers' : 'Add Modifiers'}</span>
                                                            <span>{isModifierOpen ? '▲' : '▼'}</span>
                                                        </button>
                                                    )}
                                                    {hasItems && (
                                                        <button
                                                            className="drop-down-menu"
                                                            onClick={() => toggleItems(item)}
                                                            style={{
                                                                flex: 1,
                                                                background: validationTriggered && checkItemsModifierError(item) ? '#fef2f2' : `${eventDetails?.primaryColor || '#2563eb'}0a`,
                                                                borderColor: validationTriggered && checkItemsModifierError(item) ? '#ef4444' : `${eventDetails?.primaryColor || '#2563eb'}30`,
                                                                color: validationTriggered && checkItemsModifierError(item) ? '#ef4444' : eventDetails?.primaryColor || '#2563eb',
                                                                marginTop: '8px',
                                                                minWidth: '120px'
                                                            }}
                                                        >
                                                            <span>{isItemsOpen ? 'Close Items' : 'Add Items'}</span>
                                                            <span>{isItemsOpen ? '▲' : '▼'}</span>
                                                        </button>
                                                    )}
                                                </div>

                                                {(isModifierOpen || isItemsOpen) && (
                                                    <div style={{
                                                        marginTop: '12px',
                                                        padding: '16px',
                                                        background: `${eventDetails?.primaryColor || '#2563eb'}08`,
                                                        borderRadius: '8px',
                                                        border: `1px solid ${eventDetails?.primaryColor || '#2563eb'}20`,
                                                    }}>
                                                        {/* Modifier Groups */}
                                                        {isModifierOpen && productDef.modifierGroups?.map(mg => {
                                                            const isGroupErr = validationTriggered && checkGroupError(item, mg);
                                                            return (
                                                                <div key={mg.gid} style={{ marginBottom: '12px' }}>
                                                                    <div style={{
                                                                        fontSize: '11px',
                                                                        fontWeight: '600',
                                                                        color: isGroupErr ? '#ef4444' : '#000000',
                                                                        marginBottom: '8px',
                                                                        borderBottom: isGroupErr ? '1px solid #fee2e2' : '1px solid #e2e8f0',
                                                                        paddingBottom: '4px'
                                                                    }}>
                                                                        {mg.name} {mg.minRequired || mg.maxAllowed ? `(${mg.minRequired ? "choose " + mg.minRequired : ""} ${mg.maxAllowed ? "max " + mg.maxAllowed : ""})` : ""}
                                                                    </div>
                                                                {mg.modifiers.map(m => {
                                                                    const selected = item.lineItemModifiers?.find(lm => lm.modifierGid === m.gid);
                                                                    return (
                                                                        <div key={m.gid} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                                                            <span style={{ fontSize: '11px', color: '#1e293b' }}>{m.name} <span style={{ color: '#64748b' }}>(+${AmountDivideByHundred(m.price)})</span></span>
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                                {selected && (
                                                                                    <button onClick={() => removeModifier(item, m)} style={{ padding: '0px 8px', background: '#fff', border: `1px solid ${eventDetails?.primaryColor || '#2563eb'}`, borderRadius: '4px', cursor: 'pointer', fontSize: '13px', color: eventDetails?.primaryColor || '#2563eb' }}>–</button>
                                                                                )}
                                                                                {selected && <span style={{ fontSize: '12px', fontWeight: '600' }}>{selected.quantity}</span>}
                                                                                {(() => {
                                                                                    // Check individual limit
                                                                                    if (m.maxStackableQuantity && selected && selected.quantity >= m.maxStackableQuantity) return null;
                                                                                    // Check group total limit
                                                                                    if (mg.maxAllowed) {
                                                                                        const groupTotal = item.lineItemModifiers?.reduce((sum, lm) => {
                                                                                            const isInGroup = mg.modifiers.some(gm => gm.gid === lm.modifierGid);
                                                                                            return isInGroup ? sum + lm.quantity : sum;
                                                                                        }, 0) || 0;
                                                                                        if (groupTotal >= mg.maxAllowed) return null;
                                                                                    }
                                                                                    return (
                                                                                        <button onClick={() => addModifier(item, m, mg)} style={{ padding: '0px 8px', background: '#fff', border: `1px solid ${eventDetails?.primaryColor || '#2563eb'}`, borderRadius: '4px', cursor: 'pointer', fontSize: '13px', color: eventDetails?.primaryColor || '#2563eb' }}>+</button>
                                                                                    );
                                                                                })()}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                            );
                                                        })}

                                                        {/* Nested Items */}
                                                        {isItemsOpen && productDef.items?.map(ni => (
                                                            <div key={ni.gid} style={{ marginBottom: '12px' }}>
                                                                <div style={{ fontSize: '11px', fontWeight: '600', color: '#475569', marginBottom: '8px', borderBottom: '1px solid #e2e8f0', paddingBottom: '4px' }}>{ni.displayName}</div>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                                                    <span style={{ fontSize: '11px', color: '#1e293b' }}>${AmountDivideByHundred(ni.price)}</span>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                        {(() => {
                                                                            const lii = item.lineItemItems?.find(lii => lii.itemGid === ni.gid);
                                                                            const hasItem = !!lii;
                                                                            const quantity = lii?.quantity || 0;

                                                                            const isMaxed = (ni.maxStackableQuantity && quantity >= ni.maxStackableQuantity) || 
                                                                                            (ni.enableStock && (
                                                                (ni.onlineStock !== undefined && quantity >= ni.onlineStock) ||
                                                                (ni.stockQuantity !== undefined && quantity >= ni.stockQuantity)
                                                            ));

                                                                            return (
                                                                                <>
                                                                                    {hasItem && (
                                                                                        <button onClick={() => removeNestedItem(item, { itemGid: ni.gid })} style={{ padding: '0px 8px', background: '#fff', border: `1px solid ${eventDetails?.primaryColor || '#2563eb'}`, borderRadius: '4px', cursor: 'pointer', fontSize: '13px', color: eventDetails?.primaryColor || '#2563eb' }}>–</button>
                                                                                    )}
                                                                                    {hasItem && <span style={{ fontSize: '12px', fontWeight: '600' }}>{quantity}</span>}
                                                                                    {!isMaxed && (
                                                                                        <button onClick={() => addNestedItem(item, ni)} style={{ padding: '0px 8px', background: '#fff', border: `1px solid ${eventDetails?.primaryColor || '#2563eb'}`, borderRadius: '4px', cursor: 'pointer', fontSize: '13px', color: eventDetails?.primaryColor || '#2563eb' }}>+</button>
                                                                                    )}
                                                                                </>
                                                                            );
                                                                        })()}
                                                                    </div>
                                                                </div>

                                                                {/* Nested Item Modifiers */}
                                                                {(() => {
                                                                    const lii = item.lineItemItems?.find(li => li.itemGid === ni.gid);
                                                                    if (!lii || !ni.modifierGroups?.length) return null;

                                                                    return ni.modifierGroups.map(nmg => {
                                                                        const isNestedGroupErr = validationTriggered && checkNestedGroupError(lii, nmg);
                                                                        return (
                                                                            <div key={nmg.gid} style={{ marginLeft: '12px', marginTop: '8px', paddingLeft: '8px', borderLeft: isNestedGroupErr ? '1px solid #fecaca' : '1px solid #e2e8f0' }}>
                                                                                <div style={{ fontSize: '10px', color: isNestedGroupErr ? '#ef4444' : '#000000', marginBottom: '4px' }}>
                                                                                    {nmg.name} {nmg.minRequired || nmg.maxAllowed ? `(${nmg.minRequired ? "choose " + nmg.minRequired : ""} ${nmg.maxAllowed ? "max " + nmg.maxAllowed : ""})` : ""}
                                                                                </div>
                                                                            {nmg.modifiers.map(nm => {
                                                                                const selected = lii.lineItemModifiers?.find(lm => lm.modifierGid === nm.gid);
                                                                                return (
                                                                                    <div key={nm.gid} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                                                                                        <span style={{ fontSize: '10px', color: '#475569' }}>{nm.name}</span>
                                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                                            {selected && <button onClick={() => removeNestedItemModifier(item, lii, nm)} style={{ padding: '0 4px', fontSize: '10px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '2px', cursor: 'pointer' }}>–</button>}
                                                                                            {selected && <span style={{ fontSize: '10px' }}>{selected.quantity}</span>}
                                                                                            {(() => {
                                                                                                // Individual limit
                                                                                                if (nm.maxStackableQuantity && selected && selected.quantity >= nm.maxStackableQuantity) return null;
                                                                                                // Group limit
                                                                                                if (nmg.maxAllowed) {
                                                                                                    const groupTotal = lii.lineItemModifiers?.reduce((sum, lm) => {
                                                                                                        const isInGroup = nmg.modifiers.some(gm => gm.gid === lm.modifierGid);
                                                                                                        return isInGroup ? sum + lm.quantity : sum;
                                                                                                    }, 0) || 0;
                                                                                                    if (groupTotal >= nmg.maxAllowed) return null;
                                                                                                }
                                                                                                return (
                                                                                                    <button onClick={() => addNestedItemModifier(item, lii, nm, nmg)} style={{ padding: '0 4px', fontSize: '10px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '2px', cursor: 'pointer' }}>+</button>
                                                                                                );
                                                                                            })()}
                                                                                        </div>
                                                                                    </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                    );
                                                                });
                                                                })()}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            ))
                        )}
                    </div>

                    <div className="figma-desktop-only" style={{ padding: '24px', background: '#f8fafc', borderTop: '1px solid #f1f5f9', flexShrink: 0 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
                            {subtotal > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '14px', color: '#64748b' }}>Subtotal</span>
                                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#1e293b' }}>${parseFloat(subtotal).toFixed(2)}</span>
                                </div>
                            )}
                            {serviceCharge > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '14px', color: '#64748b' }}>Service Fee</span>
                                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#1e293b' }}>${parseFloat(serviceCharge).toFixed(2)}</span>
                                </div>
                            )}
                            {discount > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '14px', color: '#64748b' }}>Discount</span>
                                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#ef4444' }}>- ${parseFloat(discount).toFixed(2)}</span>
                                </div>
                            )}
                             {taxes > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '14px', color: '#64748b' }}>Taxes</span>
                                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#1e293b' }}>${parseFloat(taxes).toFixed(2)}</span>
                                </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '16px' }}>Total Amount</span>
                                <span style={{ fontSize: '28px', fontWeight: '700', color: eventDetails?.primaryColor || '#2563eb', letterSpacing: '-0.02em' }}>${parseFloat(total).toFixed(2)}</span>
                            </div>
                        </div>
                        <button
                            className="figma-next-btn"
                            disabled={updates.length === 0}
                            onClick={handleNextClick}
                            style={{
                                width: '100%',
                                padding: '16px',
                                background: updates.length === 0 ? '#cbd5e1' : '#000',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '12px',
                                fontSize: '16px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                boxShadow: updates.length === 0 ? 'none' : '0 4px 6px -1px rgba(0, 0, 0, 0.2)'
                            }}
                        >
                            Next
                        </button>
                    </div>
                </div>

                {/* Mobile Sticky Footer */}
                <div className="figma-sticky-footer">
                    <div className="figma-total-info">
                        <span className="figma-total-label">Total Amount</span>
                        <span className="figma-total-value" style={{ color: eventDetails?.primaryColor || '#2563eb' }}>${parseFloat(total).toFixed(2)}</span>
                    </div>
                    <button
                        onClick={handleNextClick}
                        disabled={updates.length === 0}
                        style={{
                            background: updates.length === 0 ? '#cbd5e1' : '#000',
                            color: '#fff',
                            cursor: updates.length === 0 ? 'not-allowed' : 'pointer'
                        }}
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddProducts;